import * as AuthSession from 'expo-auth-session'
import AsyncStorage from '@react-native-async-storage/async-storage'

const getTokenFromCode = async (
  code: string,
  request: AuthSession.AuthRequest,
  redirectUri: string,
  discovery: Pick<AuthSession.DiscoveryDocument, 'tokenEndpoint'>
): Promise<AuthSession.TokenResponse> => {
  const tokenResponse: AuthSession.TokenResponse =
    await AuthSession.exchangeCodeAsync(
      {
        code,
        clientId: request.clientId,
        redirectUri,
        scopes: request.scopes,
        extraParams: {
          code_verifier: request?.codeVerifier || '',
          grant_type: AuthSession.GrantType.AuthorizationCode
        }
      },
      discovery
    )

  // update @Auth storage
  await AsyncStorage.setItem('@Auth', JSON.stringify(tokenResponse))

  return tokenResponse
}

const getPersistedAuth =
  async (): Promise<AuthSession.TokenResponse | null> => {
    const $auth = await AsyncStorage.getItem('@Auth')
    if ($auth !== null) {
      const auth: AuthSession.TokenResponse = JSON.parse($auth)
      return auth
    }

    return null
  }

const isPersistedAuthExpired = async (): Promise<boolean> => {
  const $auth = await AsyncStorage.getItem('@Auth')

  if ($auth !== null) {
    const auth = JSON.parse($auth)

    // check if the persisted token is still valid
    const isTokenFresh = AuthSession.TokenResponse.isTokenFresh(
      {
        expiresIn: auth.expiresIn,
        issuedAt: auth.issuedAt
      },
      0
    )

    // console.log('isTokenFresh:', isTokenFresh)

    return !isTokenFresh
  }

  return true
}

const getNewToken = async (
  clientId: string,
  token: AuthSession.TokenResponseConfig,
  discovery: Pick<AuthSession.DiscoveryDocument, 'tokenEndpoint'>
): Promise<AuthSession.TokenResponse> => {
  const tokenResponse = await AuthSession.refreshAsync(
    {
      clientId,
      refreshToken: token.refreshToken
    },
    discovery
  )

  // update @Auth storage
  await AsyncStorage.setItem('@Auth', JSON.stringify(tokenResponse))

  return tokenResponse
}

const revokeToken = async (
  token: AuthSession.TokenResponseConfig | null | undefined,
  discovery: Pick<AuthSession.DiscoveryDocument, 'revocationEndpoint'>
): Promise<void> => {
  if (token) {
    await AuthSession.revokeAsync(
      {
        token: token.accessToken
      },
      discovery
    )
  }

  // clear all storages
  await AsyncStorage.removeItem('@Auth')
  await AsyncStorage.removeItem('login')

  // TODO - will remove this storage later
  await AsyncStorage.removeItem('@Initialize')
  await AsyncStorage.removeItem('@Profile')
  await AsyncStorage.removeItem('storeId')
  
}

export const authService = {
  getTokenFromCode,
  getPersistedAuth,
  isPersistedAuthExpired,
  getNewToken,
  revokeToken
}
